import rawList from "../data/words_alpha.txt?raw";
import { scoreWord } from "../utils/scrabble";

export type ScoredWord = {
  word: string;
  score: number;
  length: number;
};

export type LetterPosition = "anywhere" | "starts" | "ends";

export type WordSearchOptions = {
  letter: string;
  position?: LetterPosition;
  minLength?: number;
  maxLength?: number;
  limit?: number;
};

let ALL_WORDS: ScoredWord[] | null = null;
let WORD_CACHE: Map<string, ScoredWord[]> | null = null;

/**
 * Loads all words with their scores.
 */
function loadAllWords(): ScoredWord[] {
  if (ALL_WORDS) return ALL_WORDS;

  const lines = rawList
    .split(/\r?\n/)
    .map(s => s.trim().toUpperCase())
    .filter(Boolean);

  ALL_WORDS = lines.map(word => {
    const { total } = scoreWord(word);
    return {
      word,
      score: total,
      length: word.length
    };
  });

  return ALL_WORDS;
}

/**
 * Loads and caches all words with their scores, organized by letter.
 */
function loadWordsByLetter(): Map<string, ScoredWord[]> {
  if (WORD_CACHE) return WORD_CACHE;

  const allWords = loadAllWords();
  const letterMap = new Map<string, ScoredWord[]>();

  for (const scoredWord of allWords) {
    const lettersInWord = new Set(scoredWord.word.split(''));
    
    for (const letter of lettersInWord) {
      if (!letterMap.has(letter)) {
        letterMap.set(letter, []);
      }
      letterMap.get(letter)!.push(scoredWord);
    }
  }

  // Sort each letter's word list by: 1) score desc, 2) length desc, 3) alphabetical
  for (const words of letterMap.values()) {
    words.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.length !== a.length) return b.length - a.length;
      return a.word.localeCompare(b.word);
    });
  }

  WORD_CACHE = letterMap;
  return letterMap;
}

/**
 * Search for words based on letter, position, and length constraints.
 */
export function searchWords(options: WordSearchOptions): ScoredWord[] {
  const {
    letter,
    position = "anywhere",
    minLength = 2,
    maxLength = 15,
    limit = 10
  } = options;

  const letterUpper = letter.toUpperCase();
  const wordMap = loadWordsByLetter();
  
  let words = wordMap.get(letterUpper) || [];

  // Apply position filter
  if (position === "starts") {
    words = words.filter(w => w.word.startsWith(letterUpper));
  } else if (position === "ends") {
    words = words.filter(w => w.word.endsWith(letterUpper));
  }

  // Apply length filter
  words = words.filter(w => w.length >= minLength && w.length <= maxLength);

  return words.slice(0, limit);
}

/**
 * Get the top N highest-scoring words containing a specific letter.
 * @deprecated Use searchWords instead
 */
export function getTopWordsByLetter(letter: string, limit: number = 5): ScoredWord[] {
  return searchWords({ letter, limit });
}

/**
 * Get statistics about a letter's usage in the dictionary.
 */
export function getLetterStats(letter: string): {
  totalWords: number;
  averageScore: number;
  highestScore: number;
} {
  const letterUpper = letter.toUpperCase();
  const wordMap = loadWordsByLetter();
  const words = wordMap.get(letterUpper) || [];

  if (words.length === 0) {
    return { totalWords: 0, averageScore: 0, highestScore: 0 };
  }

  const totalScore = words.reduce((sum, w) => sum + w.score, 0);
  const averageScore = Math.round(totalScore / words.length);
  const highestScore = words[0]?.score || 0;

  return {
    totalWords: words.length,
    averageScore,
    highestScore
  };
}

/**
 * Find all valid words that can be formed from the given letters.
 * Supports wildcards (? or *) for blank tiles.
 */
export function solveAnagram(tiles: string, minLength: number = 2, maxLength: number = 15): ScoredWord[] {
  const allWords = loadAllWords();
  const tilesUpper = tiles.toUpperCase().replace(/\s/g, '');
  
  // Count available letters (including wildcards)
  const availableLetters = new Map<string, number>();
  let wildcardCount = 0;
  
  for (const char of tilesUpper) {
    if (char === '?' || char === '*') {
      wildcardCount++;
    } else if (/[A-Z]/.test(char)) {
      availableLetters.set(char, (availableLetters.get(char) || 0) + 1);
    }
  }

  const results: ScoredWord[] = [];

  for (const scoredWord of allWords) {
    const word = scoredWord.word;
    
    // Check length constraints
    if (word.length < minLength || word.length > maxLength) {
      continue;
    }

    // Check if word can be formed with available letters
    const letterCount = new Map<string, number>();
    for (const char of word) {
      letterCount.set(char, (letterCount.get(char) || 0) + 1);
    }

    let wildcardsNeeded = 0;
    let canForm = true;

    for (const [letter, needed] of letterCount.entries()) {
      const available = availableLetters.get(letter) || 0;
      if (available < needed) {
        wildcardsNeeded += needed - available;
      }
    }

    if (wildcardsNeeded > wildcardCount) {
      canForm = false;
    }

    if (canForm) {
      results.push(scoredWord);
    }
  }

  // Sort by: 1) score desc, 2) length desc, 3) alphabetical
  results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (b.length !== a.length) return b.length - a.length;
    return a.word.localeCompare(b.word);
  });

  return results;
}
